/**
 * Wires `settlements.model.ts` to the covenant ledger's reads and writes.
 *
 * Calls `fetchGallerySettlements` for the subject and `fetchGalleryPlacements`
 * for the locked group — the second swallows its failure, since context must
 * not blank a ledger that has already arrived — then `signWorkThumbnails` in
 * a second pass. Writes are `settleResale` for the collect and remit legs,
 * with a rate on the collect leg when one is needed, and `unlockCovenant`,
 * which is addressed to the transfer rather than the settlement. Every write
 * is followed by a refetch, never a patch.
 *
 * The state the model cannot hold is which row a request is in flight for,
 * which row's rate dialog is open, and the reload counter that re-runs the
 * one effect owning each request.
 */

import { useCallback, useEffect, useState } from "react";
import type { PlacedWork, Settlement } from "@ondo/shared";

import {
  fetchGalleryPlacements,
  fetchGallerySettlements,
  settleResale,
  unlockCovenant,
} from "../lib/api.ts";
import { signWorkThumbnails } from "../lib/workImages.ts";
import { describeSettlementsError, toSettlementsView } from "./settlements.model.ts";
import type { SettlementsView } from "./settlements.model.ts";

export interface SettlementsController {
  view: SettlementsView;
  /** The settlement or transfer a request is in flight for, or null. */
  working: string | null;
  /** The row whose rate dialogue is open, or null. */
  rating: string | null;
  openRate: (settlementId: string) => void;
  closeRate: () => void;
  /** State a rate and record the collection in one press. */
  stateRate: (settlementId: string, rate: number) => void;
  settle: (settlementId: string, leg: "collect" | "remit") => void;
  /** Lift a covenant's lock. Addressed to the TRANSFER, not the settlement. */
  lift: (transferId: string) => void;
}

/**
 * The covenant ledger, and the two acts a gallery may take on it.
 *
 * TWO READS, not one, and they answer different questions. `settlements` is
 * what the covenants have already produced; `placements` is read for the ONE
 * thing settlements cannot show — a covenant still running, which is the group
 * a director will look here for when a settlement they expected is missing. A
 * screen deriving the locked group from settlements would list only the locks
 * that had already been lifted.
 *
 * REFETCHED AFTER EVERY WRITE rather than patched in place. A settlement's
 * figures are frozen in Postgres and the row that comes back is the record;
 * mutating a local copy would put a second opinion about money in the browser,
 * which is the one place this codebase least wants one.
 *
 * @param workBase '/gallery/ONDO-6624/work' — where a row links to.
 */
export function useSettlementsController(workBase: string): SettlementsController {
  const [settlements, setSettlements] = useState<Settlement[] | null>(null);
  const [placements, setPlacements] = useState<PlacedWork[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<Map<string, string>>(new Map());
  const [working, setWorking] = useState<string | null>(null);
  const [rating, setRating] = useState<string | null>(null);
  // Bumped after a write, which is what re-runs the read below. A counter
  // rather than calling the fetch again inside each handler: one effect owns
  // the request, so there is one place a failure is handled and one guard
  // against a response landing after the screen has gone.
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let active = true;

    fetchGallerySettlements()
      .then((found) => {
        if (active) setSettlements(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeSettlementsError(cause));
      });

    return () => {
      active = false;
    };
  }, [reloads]);

  /**
   * The gallery's own sales, for the locked group.
   *
   * A FAILURE HERE IS SWALLOWED, and that is the difference from the read
   * above. This screen's subject is what is owed; live covenants are context.
   * Letting a second request's failure blank a ledger that had already arrived
   * would be the supporting evidence deciding whether the evidence shows.
   */
  useEffect(() => {
    let active = true;

    fetchGalleryPlacements()
      .then((found) => {
        if (active) setPlacements(found);
      })
      .catch(() => {
        if (active) setPlacements([]);
      });

    return () => {
      active = false;
    };
  }, [reloads]);

  /**
   * The photographs, signed after the rows arrive — the second pass every list
   * in this app makes. A storage round trip must not hold up a screen whose
   * subject is money, and a path that fails to sign leaves an empty frame:
   * absent rather than broken.
   */
  useEffect(() => {
    if (!settlements) return;
    let active = true;

    const works = [
      ...settlements.map((each) => ({ id: each.workId, imagePath: each.imagePath })),
      ...(placements ?? []).map((each) => ({
        id: each.workId,
        imagePath: each.imagePath,
      })),
    ];

    void signWorkThumbnails(works).then((signed) => {
      if (active) setImages(signed);
    });

    return () => {
      active = false;
    };
  }, [settlements, placements]);

  const run = useCallback(async (key: string, act: () => Promise<void>) => {
    setWorking(key);
    try {
      await act();
      setError(null);
      setReloads((n) => n + 1);
    } catch (cause: unknown) {
      // The refusals a director will actually meet are 422s carrying the
      // database's own sentence — "state the rate", "record the collection
      // before the remittance", "the resale behind this settlement was
      // withdrawn". Showing that sentence is better than translating it into a
      // vaguer one here.
      setError(describeSettlementsError(cause));
    } finally {
      setWorking(null);
    }
  }, []);

  return {
    view: toSettlementsView({ settlements, placements, error, images, workBase }),
    working,
    rating,
    openRate: setRating,
    closeRate: () => setRating(null),
    stateRate: (settlementId, rate) => {
      setRating(null);
      void run(settlementId, () =>
        settleResale(settlementId, { leg: "collect", fxRate: rate }),
      );
    },
    settle: (settlementId, leg) => {
      void run(settlementId, () => settleResale(settlementId, { leg }));
    },
    lift: (transferId) => {
      void run(transferId, () => unlockCovenant(transferId));
    },
  };
}
